/**
 * Custom video show — category tabs, see more, YouTube/Vimeo modal.
 * No Google API / Data API — embeds use public watch/player URLs only.
 */
(function () {
  'use strict';

  function CustomVideoShow(root) {
    this.root = root;
    this.tabs = root.querySelector('[data-cvs-tabs]');
    this.grid = root.querySelector('[data-cvs-grid]');
    this.cards = Array.prototype.slice.call(root.querySelectorAll('[data-cvs-card]'));
    this.moreWrap = root.querySelector('[data-cvs-more-wrap]');
    this.moreBtn = root.querySelector('[data-cvs-more]');
    this.moreLabel = root.querySelector('[data-cvs-more-label]');
    this.noResults = root.querySelector('[data-cvs-no-results]');
    this.modal = root.querySelector('[data-cvs-modal]');
    this.modalFrame = root.querySelector('[data-cvs-modal-frame]');
    this.initialVisible = Math.max(1, parseInt(root.getAttribute('data-initial-visible'), 10) || 4);
    this.seeMoreLabel = root.getAttribute('data-see-more-label') || 'See more';
    this.seeLessLabel = root.getAttribute('data-see-less-label') || 'See less';
    this.activeTab = 'all';
    this.expanded = false;
    this.lastFocus = null;
    this._onKeyDown = this.onKeyDown.bind(this);

    this.bind();
    this.hydrateYoutubeThumbs();
    this.applyFilter();
  }

  CustomVideoShow.prototype.hydrateYoutubeThumbs = function () {
    this.root.querySelectorAll('[data-yt-thumb]').forEach(function (slot) {
      var id = slot.getAttribute('data-yt-thumb');
      if (!id) return;

      var img = document.createElement('img');
      img.className = 'custom-video-show__thumb-img';
      img.alt = slot.getAttribute('data-yt-alt') || '';
      img.loading = 'lazy';
      img.width = 480;
      img.height = 360;

      var candidates = [
        'https://i.ytimg.com/vi/' + id + '/hqdefault.jpg',
        'https://i.ytimg.com/vi/' + id + '/sddefault.jpg',
        'https://i.ytimg.com/vi/' + id + '/mqdefault.jpg'
      ];
      var index = 0;

      function tryNext() {
        if (index >= candidates.length) return;
        var url = candidates[index++];
        img.onload = function () {
          if (img.naturalWidth && img.naturalWidth < 120 && index < candidates.length) {
            tryNext();
          }
        };
        img.onerror = tryNext;
        img.src = url;
      }

      slot.innerHTML = '';
      slot.appendChild(img);
      tryNext();
    });
  };

  CustomVideoShow.prototype.bind = function () {
    var self = this;

    if (this.tabs) {
      this.tabs.addEventListener('click', function (event) {
        var tab = event.target.closest('[data-tab]');
        if (!tab || !self.tabs.contains(tab)) return;
        event.preventDefault();
        self.setActiveTab(tab.getAttribute('data-tab') || 'all');
      });
    }

    this.root.addEventListener('click', function (event) {
      var playBtn = event.target.closest('[data-cvs-play]');
      if (playBtn && self.root.contains(playBtn)) {
        event.preventDefault();
        var card = playBtn.closest('[data-cvs-card]');
        if (card) self.openModal(card);
        return;
      }

      var closeBtn = event.target.closest('[data-cvs-modal-close]');
      if (closeBtn && self.root.contains(closeBtn)) {
        event.preventDefault();
        self.closeModal();
      }
    });

    if (this.moreBtn) {
      this.moreBtn.addEventListener('click', function () {
        self.expanded = !self.expanded;
        self.applyVisibility();
      });
    }
  };

  CustomVideoShow.prototype.setActiveTab = function (tabValue) {
    this.activeTab = tabValue || 'all';
    this.expanded = false;

    if (this.tabs) {
      this.tabs.querySelectorAll('[data-tab]').forEach(function (tab) {
        var isActive = tab.getAttribute('data-tab') === tabValue;
        tab.classList.toggle('is-active', isActive);
        if (isActive) {
          tab.setAttribute('aria-current', 'page');
        } else {
          tab.removeAttribute('aria-current');
        }
      });
    }

    this.applyFilter();
  };

  CustomVideoShow.prototype.matchingCards = function () {
    var tab = this.activeTab;
    return this.cards.filter(function (card) {
      if (tab === 'all') return true;
      var cats = (card.getAttribute('data-categories') || '').trim();
      if (!cats) return false;
      return (' ' + cats + ' ').indexOf(' ' + tab + ' ') !== -1;
    });
  };

  CustomVideoShow.prototype.applyFilter = function () {
    var matching = this.matchingCards();

    this.cards.forEach(function (card) {
      var matches = matching.indexOf(card) !== -1;
      card.setAttribute('data-cvs-match', matches ? 'true' : 'false');
      if (!matches) {
        card.classList.add('is-hidden');
      }
    });

    if (this.noResults) {
      this.noResults.hidden = matching.length > 0 || this.cards.length === 0;
    }

    this.applyVisibility();
  };

  CustomVideoShow.prototype.applyVisibility = function () {
    var matching = this.matchingCards();
    var limit = this.expanded ? matching.length : this.initialVisible;

    matching.forEach(function (card, index) {
      if (index < limit) {
        card.classList.remove('is-hidden');
      } else {
        card.classList.add('is-hidden');
      }
    });

    var showMore = matching.length > this.initialVisible;
    if (this.moreWrap) {
      this.moreWrap.hidden = !showMore;
    }

    if (this.moreBtn && this.moreLabel) {
      this.moreBtn.classList.toggle('is-expanded', this.expanded);
      this.moreLabel.textContent = this.expanded ? this.seeLessLabel : this.seeMoreLabel;
      this.moreBtn.setAttribute('aria-expanded', this.expanded ? 'true' : 'false');
    }
  };

  CustomVideoShow.prototype.extractYoutubeId = function (value) {
    var str = String(value || '').trim();
    if (!str) return '';
    if (/^[\w-]{11}$/.test(str)) return str;

    var patterns = [
      /youtube\.com\/shorts\/([\w-]{11})/i,
      /youtu\.be\/([\w-]{11})/i,
      /youtube\.com\/watch\?[^#]*v=([\w-]{11})/i,
      /youtube\.com\/embed\/([\w-]{11})/i
    ];

    for (var i = 0; i < patterns.length; i++) {
      var match = str.match(patterns[i]);
      if (match && match[1]) return match[1];
    }
    return '';
  };

  CustomVideoShow.prototype.extractVimeoId = function (value) {
    var str = String(value || '').trim();
    if (!str) return '';
    if (/^\d+$/.test(str)) return str;

    var match = str.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
    return match && match[1] ? match[1] : '';
  };

  CustomVideoShow.prototype.buildEmbedSrc = function (card) {
    var url = card.getAttribute('data-video-url') || '';
    var ytId = card.getAttribute('data-youtube-id') || this.extractYoutubeId(url);
    var vimeoId = card.getAttribute('data-vimeo-id') || this.extractVimeoId(url);

    if (ytId) {
      return 'https://www.youtube.com/embed/' + encodeURIComponent(ytId) + '?autoplay=1&rel=0';
    }
    if (vimeoId) {
      return 'https://player.vimeo.com/video/' + encodeURIComponent(vimeoId) + '?autoplay=1';
    }
    return '';
  };

  CustomVideoShow.prototype.openModal = function (card) {
    if (!this.modal || !this.modalFrame) return;

    var src = this.buildEmbedSrc(card);
    if (!src) {
      var fallback = card.getAttribute('data-video-url');
      if (fallback) window.open(fallback, '_blank', 'noopener');
      return;
    }

    this.lastFocus = document.activeElement;
    this.modalFrame.innerHTML =
      '<iframe src="' +
      src +
      '" title="Video player" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen loading="lazy"></iframe>';

    this.modal.hidden = false;
    document.body.classList.add('cvs-modal-open');
    document.addEventListener('keydown', this._onKeyDown);

    var dialog = this.modal.querySelector('.custom-video-show__modal-dialog');
    if (dialog) dialog.focus();
  };

  CustomVideoShow.prototype.closeModal = function () {
    if (!this.modal || !this.modalFrame) return;
    this.modal.hidden = true;
    this.modalFrame.innerHTML = '';
    document.body.classList.remove('cvs-modal-open');
    document.removeEventListener('keydown', this._onKeyDown);

    if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
      this.lastFocus.focus();
    }
  };

  CustomVideoShow.prototype.onKeyDown = function (event) {
    if (event.key === 'Escape') {
      this.closeModal();
    }
  };

  function initAll() {
    document.querySelectorAll('[data-custom-video-show]').forEach(function (root) {
      if (root.getAttribute('data-cvs-init') === 'true') return;
      root.setAttribute('data-cvs-init', 'true');
      new CustomVideoShow(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target;
    if (!section) return;
    section.querySelectorAll('[data-custom-video-show]').forEach(function (root) {
      root.removeAttribute('data-cvs-init');
    });
    initAll();
  });
})();
